import { createStore } from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import createMultiTabState from 'vuex-multi-tab-state'

import company from './modules/company'
import notifications from './modules/notifications'
import inventory from './modules/inventory'
import filecategories from './modules/filecategories'
import articles from './modules/articles'
import exportSelection from './modules/exportSelection'
import providers from './modules/providers'
import tips from './modules/tips'
import staff from './modules/staff'
import licences from './modules/licences'
import clientSuggestionLoader from './modules/clientSuggestionLoader'
import businessSetup from './modules/businessSetup'
import files from './modules/files'

/**
 * Function to reset state to default.
 * Can be reused inside each VueX module
 * @param state: state object
 * @param statesToReset: keys to reset in the state
 * @param defaultState: the state object to reset keys to
 */
export function reset (state, statesToReset, defaultState) {
  for (const ele of statesToReset) {
    if (defaultState[ele] instanceof Array) {
      state[ele] = []
    }
    else if (defaultState[ele] instanceof Object) {
      state[ele] = Object.assign({}, defaultState[ele])
    }
    else {
      state[ele] = defaultState[ele]
    }
  }
}

/**
 * If the dispatcher is loading, make any other functions which call the dispatcher wait
 * @param state
 * @param key
 */
export function wait (state, key) {
  const timer = (resolve) => {
    setTimeout(() => {
      if (!state[key]) {
        resolve()
      }
      else {
        timer(resolve)
      }
    }, 1000)
  }
  return new Promise(resolve => timer(resolve))
}

const LOGIN = 'LOGIN'
const LOGOUT = 'LOGOUT'
const REFRESH = 'REFRESH'

/**
 * Keys within this main VueX state which will be reset
 * When the user logs out
 * @type {function(): *}
 */
const loggedInStates = () => {
  return [
    'user_details',
    'newbie',
    'permissions',
    'access_token',
    'last_socket_update',
    'guid',
    'portal',
    'temp',
    'settings'
  ]
}

const defaultStates = () => {
  return {
    /**
     * Persisted Data
     */
    user_details: {
      id: -1,
      name: 'John Smith',
      first_name: 'John',
      last_name: 'Smith',
      initials: 'JS',
      color: '#027be3',
      email: 'john@smith.com',
      created_company: false,
      signature: null,
      profile_image: null,
      intercom: null,
      calendar_save_date_onleave: true,
      calendar_create_document_go_back: true,
      calendar_use_horizontal_scrolling: false,
      calendar_slot_duration: 30,
      unverified_email: null,
      is_setting_up: false
    },
    // System settings loaded from the backend
    system_settings: {
      upload_limit: 20,
      post_max_size: 40,
      max_file_size: 40,
      timezones: [],
      notifications: {},
      icons: [],
      countries: [],
      drawing_assets_pin_limit: 20
    },
    newbie: [],
    permissions: [],
    dataloader_done: false,
    access_token: null,
    last_socket_update: null,
    guid: null,
    portal: null,
    maintenance: null,
    settings: {
      tour: false,
      menubar: true,
      widgetClient: null,
      widgetAppointment: null,
      dashboardMessage: null,
      mkey: null,
      fileDrawerOpen: true,
      calendarLastDate: null,
      calendarSelectedStaff: [],
      calendarStaffScheduleVertical: false,
      calendarUnscheduledSort: 'start_date',
      drawingToolHideAssetLabels: true,
      incomplete_report: null,
      incomplete_form: null
    },
    temp: {}
  }
}

export const store = createStore({
  // enable strict mode (adds overhead!)
  // for dev mode and --debug builds only
  strict: process.env.DEBUGGING,
  state: defaultStates(),
  plugins: [
    // These keys will be persisted in localstorage
    // Must include the keys of any modules state objects which need to be persisted
    createPersistedState({
      paths: [
        'user_details',
        'newbie',
        'permissions',
        'access_token',
        'last_socket_update',
        'guid',
        'portal',
        'temp',
        'settings',
        'company.company',
        'company.company_newbie',
        'tips.tips'
      ]
    }),
    // These keys allow the user to reuse the same access token across
    //  multiple tabs/windows on the same browser
    createMultiTabState({
      statesPaths: [
        'access_token',
        'last_socket_update',
        'guid',
        'portal'
      ]
    })
  ],
  modules: {
    company,
    notifications,
    inventory,
    filecategories,
    articles,
    exportSelection,
    providers,
    tips,
    staff,
    licences,
    clientSuggestionLoader,
    businessSetup,
    files
  },
  mutations: {
    [LOGIN] (state, value) {
      state.access_token = value.access_token
      state.portal = value.portal
    },
    [LOGOUT] (state) {
      reset(state, loggedInStates(), defaultStates())
      // guid is not persisted in localstorage
      state.guid = null
    },
    [REFRESH] (state, value) {
      state.access_token = value.token
    },
    setLastSocketUpdate (state, value) {
      state.last_socket_update = parseInt(value)
    },
    setUserData (state, value) {
      Object.assign(state.user_details, value)
    },
    setNewbie (state, { values = [], mode = 'add' }) {
      if (!Array.isArray(values)) {
        values = [values]
      }
      for (const ele of values) {
        if (mode === 'add' && !state.newbie.includes(ele)) {
          state.newbie.push(ele)
        }
        else if (mode === 'remove') {
          const eleIndex = state.newbie.indexOf(ele)
          if (eleIndex > -1) {
            state.newbie.splice(eleIndex, 1)
          }
        }
      }
    },
    socketClearUserData (state) {
      Object.assign(state.user_details, defaultStates().user_details)
      state.newbie = []
      state.permissions.splice(0, state.permissions.length)
    },
    setGuid (state, value) {
      state.guid = value.guid
    },
    setPermissions (state, value) {
      state.permissions = [...value]
    },
    setSetting (state, { key, value }) {
      state.settings[key] = value
    },
    setMaintenance (state, value) {
      state.maintenance = value
    },
    setTemp (state, value) {
      state.temp = Object.assign(state.temp, JSON.parse(JSON.stringify(value)))
    },
    setSystemSettings (state, value = {}) {
      state.system_settings = {
        ...state.system_settings,
        ...value
      }
    },
    setDataloaderDone (state, val) {
      state.dataloader_done = Boolean(val)
    },
    clearTemp (state, keys) {
      if (typeof keys === 'string') {
        keys = [keys]
      }
      for (const ele in keys) {
        delete state.temp[keys[ele]]
      }
    }
  },
  actions: {
    login ({ state, commit, rootState }, creds) {
      return new Promise(resolve => {
        commit(LOGIN, creds)
        resolve()
      })
    },
    // Reset modules when the user logs out
    logout ({ commit }) {
      return new Promise(resolve => {
        commit(LOGOUT)
        commit('company/reset')
        commit('notifications/reset')
        commit('inventory/reset')
        commit('filecategories/reset')
        commit('articles/reset')
        commit('exportSelection/reset')
        commit('providers/reset')
        commit('tips/reset')
        commit('staff/reset')
        commit('licences/reset')
        commit('clientSuggestionLoader/reset')
        commit('businessSetup/reset')
        commit('files/reset')
        resolve()
      })
    },
    // Clear the cordova/capacitor state for relevant modules when the app is reopened from a soft-closed state
    nativeAppClear ({ commit }) {
      return new Promise(resolve => {
        commit('inventory/reset')
        commit('filecategories/reset')
      })
    },
    refresh ({ commit, state }, tokenData) {
      return new Promise(resolve => {
        commit(REFRESH, tokenData)
        resolve()
      })
    },
    lastSeen ({ commit, state }, data) {
      return new Promise(resolve => {
        commit('setLastSocketUpdate', data)
        resolve()
      })
    },
    socketNeedsUpdating ({ commit }) {
      return new Promise(resolve => {
        commit('socketClearUserData')
        commit('company/reset')
        resolve()
      })
    },
    guid ({ commit }, data) {
      return new Promise(resolve => {
        commit('setGuid', data)
        resolve()
      })
    },
    getTemp ({ commit, state }, key) {
      let keepVal = false
      let val
      if (typeof key === 'string') {
        val = key
      }
      else {
        keepVal = key.keep || false
        val = key.key
      }
      return new Promise(resolve => {
        const value = state.temp[val]
        if (value) {
          if (!keepVal) {
            commit('clearTemp', val)
          }
          resolve(JSON.parse(JSON.stringify(value)))
        }
        resolve(null)
      })
    }
  },
  getters: {
    isLoggedIn: state => Boolean(state.access_token !== null),
    getPortal: state => state.portal,
    getUserData: state => state.user_details,
    getAccessToken: state => state.access_token,
    getLastSocketUpdate: state => state.last_socket_update,
    getGuid: state => state.guid,
    hasPermission: (state) => (perm) => {
      return state.permissions.indexOf(perm) !== -1
    },
    getNewbie: state => state.newbie,
    getTemp: state => state.temp,
    getMaintenance: state => state.maintenance,
    getSetting: (state) => (key) => {
      return state.settings[key]
    },
    getDataloaderDone: state => state.dataloader_done,
    getSystemSetting: (state) => (key) => {
      return state.system_settings[key]
    }
  }
})

export default () => store
